import { Descriptions } from "@/components/v2";
import { Card } from "@rumbledirect/ui";
import { type DescriptionsProps, Button, Spin, Space, Popconfirm } from "antd";
import { Link } from "react-router-dom";
import type { ImageCapture } from "@rumble-it/common-lib/types/models/ImageCapture.interface";
import { UseMutationResult } from "@tanstack/react-query";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

interface ImageCaptureDetailCardProps {
  imageCapture: ImageCapture | null | undefined;
  isLoading: boolean;
  deleteMutation?: UseMutationResult<null, Error, void, unknown>;
}

export const ImageCaptureDetailCard = ({
  imageCapture,
  isLoading,
  deleteMutation,
}: ImageCaptureDetailCardProps) => {
  if (isLoading) return <Spin />;
  if (!imageCapture) return <div>Geen gegevens gevonden.</div>;

  return (
    <Card
      title={`ImageCapture: ${imageCapture?.title || imageCapture?.["@id"]}`}
      extra={
        <Space>
          <Link to={`/image-captures/${imageCapture?.id}/edit`}>
            <Button icon={<EditOutlined />} />
          </Link>
          <Popconfirm
            title="ImageCapture verwijderen"
            description="Weet je zeker dat je deze ImageCapture wilt verwijderen?"
            onConfirm={() => deleteMutation?.mutate()}
            okText="Ja"
            cancelText="Nee"
          >
            <Button
              loading={deleteMutation?.isPending}
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      }
    >
      <Descriptions
        items={
          [
            { label: "Titel", children: imageCapture.title ?? null },
            { label: "Account", children: imageCapture.account?.title ?? null },
            { label: "Land", children: imageCapture.settings?.country ?? null },
            {
              label: "Taal",
              children: imageCapture.settings?.language ?? null,
            },
            {
              label: "Time zone",
              children: imageCapture.settings?.timeZone ?? null,
            },
            {
              label: "Link expiry days",
              children: imageCapture.settings?.linkExpiryDays ?? null,
            },
            ...(imageCapture.settings?.styling
              ? Object.entries(imageCapture.settings.styling).map(
                  ([key, value]) => ({
                    key,
                    label: `Styling: ${key}`,
                    children:
                      key.toLowerCase() === "logo" ? "- logo -" : String(value),
                  }),
                )
              : []),
            ...(imageCapture.settings?.settings?.map(
              (setting: { key: string; value: string }) => ({
                key: setting.key,
                label: `Setting: ${setting.key}`,
                children: setting.value,
              }),
            ) ?? []),
          ] satisfies DescriptionsProps["items"]
        }
      />
    </Card>
  );
};
